import React, { useState, useEffect, useRef, FC, lazy, Suspense } from 'react';
import { Router, Route, Switch, RouteComponentProps, Redirect } from 'react-router-dom';
import BaseLayout from '@/layout/BaseLayout';
import WorkBenchRoutes from './WorkBench';

import { useDispatch } from 'react-redux';
import { dispatchUser } from '@/redux/action';
import { setGlobalCssVars } from '@/layout/themes/themeManage';
import HomePage from '@/pages/HomePage';
import { Spin, message } from 'antd';
import { getPersonInfo } from '@/api/newApi/carts';
import { scheduleLoadJAScript } from '@/dataPort';
import { fetchHomePageConfig, preloadHomePageLcpFromConfig } from '@/util/homepageConfig';
import { ensureHomepageConfig } from '@/util/ensureHomepageConfig';
import { isGuest } from '@/util/commonUtils';
import { prefetchCartFromPathname } from '@/util/cartConfig';
import { preloadLoginBannerLcpImage } from '@/util/loginBannerConfig';
import Forbidden403 from '@/pages/Forbidden403';
import { bootstrapLoggedInSession } from '@/util/sessionBootstrap';
import { appHistory } from '@/util/appHistory';
import { initChineseTextInspection, scheduleChineseTextReport } from '@/util/translationInspection';

const SearchList = lazy(() => import('../pages/SearchList'));
const SkuDetailV2 = lazy(() => import('../pages/SkuDetailV2/index'));
const Carts = lazy(() => import('../pages/CartsNew/index'));
const PalletFill = lazy(() => import('@/pages/PalletFill'));
const OrderInfo = lazy(() => import('../pages/OrderInfo/index'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const SkuUnavailable = lazy(() => import('@/pages/SkuUnavailable'));
const HomePagePreview = lazy(() => import('@/pages/HomePagePreview'));
const ActivityPagePreview = lazy(() => import('@/pages/ActivityPagePreview'));
const SelectList = lazy(() => import('../pages/SelectList'));
const BatchCarts = lazy(() => import('@/pages/BatchCarts'));
const AboutPage = lazy(() => import('@/pages/About'));
const AfterSalesPolicy = lazy(() => import('@/pages/AfterSalesPolicy'));
const SearchAi = lazy(() => import('@/pages/SearchAi'));
const SearchAiProductDetail = lazy(() => import('@/pages/SearchAi/SearchAiProductDetail'));
const SearchAiMatchList = lazy(() => import('@/pages/SearchAi/SearchAiMatchList'));
const SearchAiRedirect: React.FC = () => <Redirect to="/rede/searchAi" />;

// 公开页不是首页/商详等主流量路径，同步 import 会把登录注册样式和依赖并进首屏包。
// lazy 后配合 craco libs chunks:'initial'，公开页依赖随页面按需加载。
const Login = lazy(() => import('../pages/Login'));
const RegistrationGuide = lazy(() => import('@/pages/RegistrationGuide'));
const Registration = lazy(() => import('@/pages/Registration'));
const RegistrationSuccess = lazy(() => import('@/pages/RegistrationSuccess'));

message.config({
  top: 'calc(50% - 20px)',
});

interface CommonComponentProps {
  isAudit?: boolean;
  isDelete?: boolean;
  isVsp?: boolean;
  isBatch?: boolean;
}

interface RouteMeta {
  title: string;
  pageId?: number;
  eventId?: string;
}

interface RouteConfig {
  path: string;
  name: string;
  exact: boolean;
  meta?: RouteMeta;
  component: React.ComponentType<CommonComponentProps & RouteComponentProps>;
}

interface RouteWithAuthProps extends RouteConfig {
  isAuthenticated: boolean | null;
  isBootstrapped: boolean;
}

const PageLoadingSpin: FC = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      minHeight: 'calc(100vh - 108px)',
    }}
  >
    <Spin size="large" />
  </div>
);

const routes: Array<{
  meta?: {
    pageId?: string;
    [index: string]: any;
  };
  [index: string]: any;
}> = [
  //搜索列表
  {
    path: `/page/list`,
    name: 'SearchList',
    exact: true,
    meta: {
      title: 'searchList', //搜索列表
      pageId: 'ProductList',
    },
    component: SearchList,
  },
  {
    path: `/`,
    name: 'Home',
    exact: true,
    meta: {
      title: 'home', //首页
      pageId: 'HomePage',
    },
    component: HomePage,
  },
  {
    path: `/homepagePreview`,
    name: 'HomePagePreview',
    exact: true,
    meta: {
      title: 'homePreview',
      pageId: 'HomePage',
      simplePage: true,
    },
    component: HomePagePreview,
  },
  {
    path: `/activityPagePreview`,
    name: 'ActivityPagePreview',
    exact: true,
    meta: {
      title: 'activityPagePreview',
      pageId: 'ActivityPage',
      simplePage: true,
    },
    component: ActivityPagePreview,
  },
  {
    path: `/select`,
    name: 'Select',
    exact: true,
    meta: {
      title: 'select', //精选商品-落地页
      pageId: 'SelectList',
    },
    component: SelectList,
  },
  //购物车
  {
    path: `/sku/:skuId`,
    name: 'SkuDetail',
    exact: true,
    meta: {
      title: 'skuDetail', //商品详情页
      pageId: 'ProductDetail',
    },
    component: SkuDetailV2,
  },
  {
    path: `/cart/carts`,
    name: 'Carts',
    exact: true,
    meta: {
      title: 'cartList', //采购清单
      background: '#F3F6F9',
      pageId: 'Cart',
    },
    component: Carts,
  },
  //政企-多货盘凑单页（F-006）：从采购清单货盘分组「去凑单」进入，query 传 palletCode/palletName
  {
    path: `/cart/palletFill`,
    name: 'PalletFill',
    exact: true,
    meta: {
      title: 'palletFill', //货盘凑单
      background: '#F3F6F9',
    },
    component: PalletFill,
  },
  {
    path: `/cart/batchAdd`,
    name: 'batchCarts',
    exact: true,
    meta: {
      title: 'batchCarts', //采购清单
      pageId: 'BatchAddCart',
    },
    component: BatchCarts,
  },
  {
    path: `/order/goOrderInfo`,
    name: 'OrderInfo',
    exact: true,
    meta: {
      title: 'settlement', //结算页
      pageId: 'Checkout',
    },
    component: OrderInfo,
  },
  {
    path: `/about`,
    name: 'about',
    exact: true,
    meta: {
      title: 'about', //结算页
      pageId: 'about',
    },
    component: AboutPage,
  },
  {
    path: `/afterSalesPolicy`,
    name: 'AfterSalesPolicy',
    exact: true,
    meta: {
      title: 'afterSalesPolicy', //结算页
      pageId: 'AfterSalesPolicy',
      // moduleName: '售后须知'
    },
    component: AfterSalesPolicy,
  },
  {
    path: `/searchAi`,
    name: 'SearchAiRedirect',
    exact: true,
    meta: {
      title: 'searchAi',
      background: '#fff',
      pageId: 'SearchAi',
    },
    component: SearchAiRedirect,
  },
  {
    path: `/rede/searchAi`,
    name: 'SearchAi',
    exact: true,
    meta: {
      title: 'searchAi',
      background: '#fff',
      pageId: 'SearchAi',
    },
    component: SearchAi,
  },
  {
    path: `/rede/searchAi/productDetail`,
    name: 'SearchAiProductDetail',
    exact: true,
    meta: {
      title: 'skuDetail',
      background: '#fff',
      pageId: 'SearchAiProductDetail',
      simplePage: true,
    },
    component: SearchAiProductDetail,
  },
  {
    path: `/rede/searchAi/matchList`,
    name: 'SearchAiMatchList',
    exact: true,
    meta: {
      title: 'searchAiMatchList',
      background: '#fff',
      pageId: 'SearchAiMatchList',
      simplePage: true,
    },
    component: SearchAiMatchList,
  },
  ...WorkBenchRoutes,
  {
    path: `/skuUnavailable`,
    name: 'SkuUnavailable',
    exact: true,
    meta: {
      title: 'skuUnavailable',
      containerMinHeight: false,
      pageId: 'SkuUnavailable',
    },
    component: SkuUnavailable,
  },
  {
    path: `/403`,
    name: 'Forbidden403',
    exact: true,
    meta: {
      title: 'forbidden403',
    },
    component: Forbidden403,
  },
  {
    path: '*',
    name: 'notFound',
    exact: true,
    meta: {
      title: 'notFound', //首页
      containerMinHeight: false,
    },
    component: NotFound,
  },
];
const unAuthPath = [
  '/passport/login',
  '/customerregistration',
  '/about',
  '/register/guide',
  '/register',
  '/register/success',
  // 首页模拟页用 URL token 校验预览权限，不强制走账号登录鉴权。
  '/homepagePreview',
  // 活动页模拟页同样使用 URL token 校验预览权限，避免未登录时被正式鉴权拦截。
  '/activityPagePreview',
  // 403 错误页不应触发登录态校验，否则未登录会被 account/me 401 弹回登录形成死循环
  '/403',
];

const RouteLoading: FC = () => (
  <div
    style={{
      minHeight: 'calc(100vh - 108px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--container-bg-color, #F8F9FC)',
    }}
  >
    <Spin size="large" />
  </div>
);

const getErrorField = (error: unknown, field: 'code' | 'message' | 'name') => {
  if (!error || typeof error !== 'object' || !(field in error)) {
    return '';
  }

  const value = (error as Record<'code' | 'message' | 'name', unknown>)[field];
  return typeof value === 'string' ? value : '';
};

const isRequestAbortedError = (error: unknown) => {
  const code = getErrorField(error, 'code');
  const message = getErrorField(error, 'message');
  const name = getErrorField(error, 'name');

  return code === 'ERR_CANCELED' || name === 'CanceledError' || /request aborted|cancelled|canceled/i.test(message);
};

const RouteWithAuth: FC<RouteWithAuthProps> = ({ component: Component, isAuthenticated, isBootstrapped, ...rest }) => (
  <Route
    {...rest}
    render={(props: RouteComponentProps) => {
      const isPublicRoute = unAuthPath.includes(rest.path);
      const isHomeRoute = rest.path === '/';

      if (!isBootstrapped && !isPublicRoute && !isHomeRoute) {
        return <PageLoadingSpin />;
      }

      if (isAuthenticated === false && !isPublicRoute) {
        return <Redirect to="/passport/login" />;
      }

      setGlobalCssVars({
        'container-bg-color': rest.meta?.background || '#F8F9FC',
      });

      document.title = TLT(`工采国际`);
      return <Component {...props} />;
    }}
  />
);

const BasicRoute: FC = () => {
  /** isBootstrapped=false 时，语言包和鉴权还在合并初始化中，先不挂载内部页面。 */
  const [{ isAuthenticated, isBootstrapped }, setRouteBootstrap] = useState<{
    isAuthenticated: boolean | null;
    isBootstrapped: boolean;
  }>({
    isAuthenticated: null,
    isBootstrapped: false,
  });
  const dispatch = useDispatch();
  // 记录是否已经初始化过配置
  const hasInitializedConfig = useRef<boolean>(false);

  // 用户认证检查函数
  const checkUserAuth = async (): Promise<boolean | null> => {
    try {
      const response: any = await getPersonInfo();
      if (response.success) {
        const value = response?.value;
        // success=true 但缺少用户实体时不能读取 value.pin，否则会被 TypeError 误判为鉴权失败。
        if (!value || typeof value !== 'object') {
          console.warn('用户鉴权响应缺少用户信息');
          return false;
        }
        scheduleLoadJAScript(value.pin, value);
        if (value) {
          dispatch(dispatchUser(value));
          // bootstrap 全局用 void 并行，不 await：购物车/商详等鉴权页可立即 isBootstrapped，
          // 避免地址接口串进进页门禁拖 LCP；bootstrapLoggedInSession 有 inflight 去重，首页仍可 await 同一 Promise。
          void bootstrapLoggedInSession(dispatch).catch(error => {
            console.error('会话 bootstrap 失败:', error);
          });
          // 只有当 userType 为 5 且是第一次调用时才初始化配置
          if ((value.userType === 3 || value.userType === 5) && !hasInitializedConfig.current) {
            hasInitializedConfig.current = true;
            import('@/api/newApi/home')
              .then(({ initConfig }) => initConfig())
              .catch(error => {
                console.error('初始化配置失败:', error);
              });
          }
        }
        return !!value;
      }
      return false;
    } catch (error) {
      if (isRequestAbortedError(error)) {
        // SPA 切路由时 account/me 可能被浏览器取消；取消不等于未登录，避免误跳登录页。
        console.warn('用户鉴权请求被取消，跳过本次登录页重定向:', error);
        return null;
      }
      console.error('用户鉴权检查失败:', error);
      return false;
    }
  };

  useEffect(() => {
    let cancelled = false;
    const pathname = window.location.pathname;
    const shouldCheckAuth = !unAuthPath.includes(pathname);
    const cleanupChineseTextInspection = initChineseTextInspection();

    const authTask = shouldCheckAuth ? checkUserAuth() : Promise.resolve<boolean | null>(null);

    if (pathname === '/') {
      // 【首页专属 LCP 链路】仅在此处 await bootstrap → ensure → preload Banner，其它路由不走这段。
      // 好处：
      // 1) 登录用户：地址上下文就绪后再拉 CMS/Banner，减少楼层数据偏差；配合 HomePage 先渲染真 Banner，降低 NavBar Logo 误当 LCP 的概率。
      // 2) 游客站：跳过 bootstrap/ensure（业务约定游客不打地址接口），直接预取 CMS，不引入多余等待。
      // 3) 与 checkUserAuth 内 void bootstrap 复用同一 inflight，首页多等、购物车/商详不被串行阻塞。
      void (async () => {
        try {
          if (shouldCheckAuth) {
            const authed = await authTask;
            if (!authed) {
              return;
            }
            if (!isGuest()) {
              // 首页登录态：等地址+车数 bootstrap 完成再 ensure，保证 CMS 预取与头部地址一致
              await bootstrapLoggedInSession(dispatch);
              await ensureHomepageConfig();
            }
          }
          if (cancelled) {
            return;
          }
          const result = await fetchHomePageConfig();
          preloadHomePageLcpFromConfig(result);
        } catch (error) {
          console.error('首页 CMS 校验或预取失败:', error);
        }
      })();
    }

    // 登录页 LCP 为 Banner：在 lazy chunk 下载前就 preload，避免等 Login 挂载后再拉图
    if (pathname === '/passport/login') {
      preloadLoginBannerLcpImage();
    }

    // 购物车权限预取：与 account/me 并行，避免 authority 串在鉴权之后（详见 cartConfig）
    if (pathname.startsWith('/cart/carts')) {
      prefetchCartFromPathname(pathname);
    }

    // SPA 内跳转到登录（如未登录 Redirect）时同样尽早 preload Banner
    const unlistenHistory = appHistory.listen(location => {
      if (location.pathname === '/passport/login') {
        preloadLoginBannerLcpImage();
      }
      scheduleChineseTextReport();
    });

    void authTask
      .then(authResult => {
        if (!cancelled) {
          setRouteBootstrap({
            isAuthenticated: shouldCheckAuth ? authResult : null,
            isBootstrapped: true,
          });
        }
      })
      .catch(error => {
        console.error('路由初始化失败:', error);
        if (!cancelled) {
          setRouteBootstrap({
            isAuthenticated: false,
            isBootstrapped: true,
          });
        }
      });

    return () => {
      cancelled = true;
      unlistenHistory();
      cleanupChineseTextInspection();
    };
  }, []);

  // 公开页在 BaseLayout 的 Suspense 之外，lazy 后必须自带边界，否则会白屏抛错
  const renderPublicPage = (page: React.ReactNode) =>
    isBootstrapped ? <Suspense fallback={<PageLoadingSpin />}>{page}</Suspense> : <PageLoadingSpin />;

  return (
    <Router history={appHistory}>
      <Switch>
        <Route exact path="/passport/login" render={() => renderPublicPage(<Login />)} />
        <Route exact path="/register/guide" render={() => renderPublicPage(<RegistrationGuide />)} />
        <Route exact path="/register" render={() => renderPublicPage(<Registration />)} />
        <Route exact path="/register/success" render={() => renderPublicPage(<RegistrationSuccess />)} />
        <BaseLayout backgroundColor="#f0f0f0" isAuth={isAuthenticated}>
          <Suspense fallback={<PageLoadingSpin />}>
            <Switch>
              {routes.map((route, i) => (
                <RouteWithAuth key={i} isAuthenticated={isAuthenticated} isBootstrapped={isBootstrapped} {...route} />
              ))}
            </Switch>
          </Suspense>
        </BaseLayout>
      </Switch>
    </Router>
  );
};

export default BasicRoute;
